import { Link, useLocation } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { isRemoteCell, type Cell } from "@/lib/cells/cells";
import { isCellSignInError } from "@/lib/cells/cell-session";
import type { CellFailure } from "@/lib/cells/cell-tenants";

/**
 * Opens the cell's sign-in. `newTab` keeps the current page, and whatever is
 * typed into it, open; its queries refetch when the tab is focused again.
 */
export function CellSignInButton({
  cellId,
  newTab = false,
}: {
  cellId: string;
  newTab?: boolean;
}) {
  const location = useLocation();
  return (
    <Button asChild size="sm" variant="outline">
      <Link
        to="/cell-sign-in"
        search={{ cell: cellId, redirect: location.href }}
        {...(newTab && { target: "_blank", rel: "noopener noreferrer" })}
      >
        Sign in to {cellId}
      </Link>
    </Button>
  );
}

/**
 * One line per cell whose tenants couldn't be listed, for the top of a tenant
 * picker, so a missing tenant isn't mistaken for one that doesn't exist.
 */
export function CellTenantsUnavailable({
  failures,
  signInInNewTab = false,
}: {
  failures: CellFailure[];
  signInInNewTab?: boolean;
}) {
  if (failures.length === 0) {
    return null;
  }

  return (
    <div role="status" className="flex flex-col gap-2 border-b p-2">
      {failures.map(({ cell, error }) => (
        <div key={cell.id} className="flex flex-col gap-1 text-xs">
          <span className="text-yellow-700 dark:text-yellow-300">
            {cell.residency} ({cell.id}) tenants aren't listed: {error.message}
          </span>
          {isRemoteCell(cell) && isCellSignInError(error) && (
            <CellSignInButton cellId={cell.id} newTab={signInInNewTab} />
          )}
        </div>
      ))}
    </div>
  );
}

const tenantsNotShown = (cell: Cell) =>
  `Tenants from ${cell.id} (${cell.residency}) aren't shown`;

export function CellFailuresAlert({
  failures,
  heading = tenantsNotShown,
  signInInNewTab = false,
}: {
  failures: CellFailure[];
  heading?: (cell: Cell) => string;
  signInInNewTab?: boolean;
}) {
  if (failures.length === 0) {
    return null;
  }

  return (
    <div className="flex shrink-0 flex-col gap-2">
      {failures.map(({ cell, error }) => (
        <div
          key={cell.id}
          role="status"
          className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20"
        >
          <AlertTriangle
            aria-hidden="true"
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400"
          />
          <div className="flex-1 space-y-1">
            <div className="font-medium text-yellow-800 dark:text-yellow-200">
              {heading(cell)}
            </div>
            <div className="text-sm text-yellow-700 dark:text-yellow-300">
              {error.message}
            </div>
          </div>
          {isRemoteCell(cell) && isCellSignInError(error) && (
            <CellSignInButton cellId={cell.id} newTab={signInInNewTab} />
          )}
        </div>
      ))}
    </div>
  );
}
